import { Download } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, useActs, useInvoices, useClientRole, useStatementOfAccount, useSubscriptions } from '@/api';
import { hasActs, useContour } from '@/lib/contour';
import type { Act, Invoice } from '@/api/types';
import { ActSignDialog, ownerSignsAct } from '@/components/ActSignDialog';
import { PayDialog } from '@/components/PayDialog';
import { DownloadButton } from '@/components/DownloadButton';
import { Dialog, Empty, Status, Tabs } from '@/components/ui';
import { tabPanel } from '@/lib/tabs';
import { QueryPending } from '@/components/QueryState';
import { balanceOf, formatDate, formatDue, formatMoney, formatQuantity, plural } from '@/lib/format';
import { actStatus, invoiceStatus, signedViaLabel } from '@/lib/status';

type Tab = 'invoices' | 'subscriptions' | 'acts';

export function Money() {
  const [params, setParams] = useSearchParams();
  const contour = useContour();
  // Acts are RU paperwork: the main contour has no acts tab (Paddle issues the buyer's documents).
  const withActs = hasActs(contour);
  const asked = (params.get('tab') as Tab) || 'invoices';
  const tab = asked === 'acts' && !withActs ? 'invoices' : asked;
  const invoicesQ = useInvoices();
  const subsQ = useSubscriptions();
  const actsQ = useActs();
  const { data: invoices } = invoicesQ;
  const { data: subs } = subsQ;
  const { data: acts } = actsQ;
  const role = useClientRole();
  const [signing, setSigning] = useState<Act>();
  const [paying, setPaying] = useState<Invoice>();
  const [picking, setPicking] = useState(false);
  const [reconcile, setReconcile] = useState(false);
  const due = (invoices?.filter((i) => i.status === 'sent' || i.status === 'overdue') ?? []).sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  const dueTotal = due.reduce((n, i) => n + balanceOf(i).amountMinor, 0);
  const toSign = acts?.filter((a) => a.status === 'to_sign').length ?? 0;

  return (
    <>
      <section className="frame mb-10 flex flex-wrap items-end justify-between gap-6 px-6 py-6">
        <div>
          <p className="text-sm text-muted">Деньги</p>
          {!invoices ? (
            <p className="mt-1 text-xl font-semibold tracking-tight">{invoicesQ.isError ? 'Счета не загрузились' : ' '}</p>
          ) : due.length > 0 ? (
            <p className="mt-1 text-xl font-semibold tracking-tight">
              К оплате <span className="num">{formatMoney({ amountMinor: dueTotal, currency: due[0]?.total.currency ?? contour?.currency ?? 'RUB' })}</span>
            </p>
          ) : (
            <p className="mt-1 text-xl font-semibold tracking-tight">Всё оплачено</p>
          )}
          <p className="mt-2 text-sm text-muted">
            {due.length > 0 && `${due.length === 1 ? 'Один счёт' : `${due.length} ${plural(due.length, 'счёт', 'счёта', 'счетов')}`}, ближайший — ${formatDue(due[0].dueAt)}. `}
            {withActs && (toSign > 0 ? `Актов на подпись: ${toSign}.` : 'Акты присылает студия — они появятся здесь.')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary" onClick={() => setReconcile(true)}>
            {withActs ? 'Акт сверки' : 'Сверка расчётов'}
          </button>
          {due.length > 0 && (
            <button className="btn-primary" onClick={() => (due.length === 1 ? setPaying(due[0]) : setPicking(true))}>
              {due.length === 1 ? `Оплатить счёт № ${due[0].number}` : 'Оплатить'}
            </button>
          )}
        </div>
      </section>

      <Tabs<Tab>
        id="money"
        label="Разделы денег"
        value={tab}
        onChange={(t) => setParams({ tab: t })}
        items={[
          { value: 'invoices', label: 'Счета', count: invoices?.length },
          { value: 'subscriptions', label: 'Регулярные платежи', count: subs?.length },
          ...(withActs ? [{ value: 'acts' as const, label: 'Акты', count: acts?.length }] : []),
        ]}
      />

      <div {...tabPanel('money', tab)} className="focus:outline-none">
      {tab === 'invoices' &&
        (!invoices ? (
          <QueryPending query={invoicesQ} />
        ) : (
          <ul className="divide-y divide-line border-y border-line">
            {invoices.map((i) => {
              const s = invoiceStatus[i.status];
              return (
                <li key={i.id} className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3.5">
                  <Link to={`/money/${i.id}`} className="min-w-0 flex-1 hover:underline max-sm:basis-full">
                    <span className="font-medium">Счёт № {i.number}</span>
                    <span className="block text-sm text-muted">
                      {i.lines[0]?.note ?? i.lines[0]?.title}{i.projectTitle ? `, ${i.projectTitle}` : ''}
                    </span>
                  </Link>
                  <span className="text-sm text-muted">{i.status === 'paid' ? `оплачен ${formatDate(i.payment?.at ?? i.dueAt)}` : `до ${formatDate(i.dueAt)}`}</span>
                  <span className="num text-right max-sm:ml-auto sm:w-28">{formatMoney(i.total)}</span>
                  <span className="flex justify-end sm:w-40">
                    {i.status === 'sent' || i.status === 'overdue' ? (
                      <button className="btn-primary" onClick={() => setPaying(i)}>
                        Оплатить
                      </button>
                    ) : (
                      <Status tone={s.tone}>{s.label}</Status>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        ))}

      {tab === 'subscriptions' &&
        (!subs ? (
          <QueryPending query={subsQ} />
        ) : !subs.length ? (
          <Empty title="Подписок нет">Регулярные услуги появятся здесь с датой следующего счёта.</Empty>
        ) : (
          <ul className="divide-y divide-line border-y border-line">
            {subs.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-4 py-4">
                <div>
                  <div className="flex items-center gap-3">
                    <Link to={`/p/${s.projectId}`} className="font-medium hover:underline">
                      {s.title}
                    </Link>
                    <Status tone={s.status === 'active' ? 'done' : 'neutral'}>{s.status === 'active' ? 'Действует' : 'На паузе'}</Status>
                  </div>
                  <div className="mt-0.5 text-sm text-muted">
                    с {formatDate(s.since)}, следующий счёт {formatDate(s.nextInvoiceAt)}
                  </div>
                </div>
                <div className="num text-md font-semibold">
                  {formatMoney(s.price)} <span className="text-sm font-normal text-muted">в месяц</span>
                </div>
              </li>
            ))}
          </ul>
        ))}

      {tab === 'acts' &&
        (!acts ? (
          <QueryPending query={actsQ} />
        ) : !acts.length ? (
          <Empty title="Актов пока нет">Когда студия отправит акт на подпись, он появится здесь.</Empty>
        ) : (
          <ul className="divide-y divide-line border-y border-line">
            {acts.map((a) => {
              const s = actStatus[a.status];
              return (
                <li key={a.id} className="flex flex-wrap items-center gap-4 py-3.5">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">
                      Акт № {a.number} от {formatDate(a.issuedAt)}
                    </div>
                    <div className="text-sm text-muted">
                      {(a.invoiceIds ?? (a.invoiceId ? [a.invoiceId] : [])).length ? (
                        <>
                          закрывает {(a.invoiceIds?.length ?? 1) === 1 ? 'счёт' : 'счета'}{' '}
                          {(a.invoiceIds ?? [a.invoiceId!]).map((id, n) => (
                            <span key={id}>
                              {n > 0 && ', '}
                              <Link className="underline decoration-strong underline-offset-2 hover:decoration-ink" to={`/money/${id}`}>
                                № {a.invoiceNumbers?.[n] ?? invoices?.find((i) => i.id === id)?.number ?? ''}
                              </Link>
                            </span>
                          ))}
                          ,{' '}
                        </>
                      ) : null}
                      <span className="num">{formatMoney(a.total)}</span>
                      {a.projectTitle ? `, ${a.projectTitle}` : ''}
                      {a.status === 'signed' && a.signedVia ? `, подписан ${signedViaLabel[a.signedVia]}` : ''}
                    </div>
                    {(a.summary || a.lines?.length) && (
                      <details className="mt-1 text-sm">
                        <summary className="cursor-pointer text-muted hover:text-ink">Что вошло в акт</summary>
                        {a.summary && <p className="mt-1 whitespace-pre-line">{a.summary}</p>}
                        <ul className="mt-1 divide-y divide-line">
                          {a.lines?.map((l, i) => (
                            <li key={i} className="flex justify-between gap-4 py-1">
                              <span>
                                {l.title}
                                {l.description ? <span className="block text-xs text-muted">{l.description}</span> : null}
                              </span>
                              <span className="num whitespace-nowrap text-muted">
                                {formatQuantity(l.quantity)} {l.unit} × {formatMoney(l.price)} = {formatMoney(l.total)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                  {a.documentId && (
                    <DownloadButton name={a.fileName} load={() => api.actFile(a.id)} className="btn-ghost" label={`Скачать `}>
                      <Download className="size-4" /> PDF
                    </DownloadButton>
                  )}
                  {a.status === 'to_sign' ? (
                    role === 'owner' ? (
                      <button className="btn-primary" onClick={() => setSigning(a)}>
                        Подписать
                      </button>
                    ) : (
                      <span className="text-sm text-muted">{ownerSignsAct}</span>
                    )
                  ) : (
                    <Status tone={s.tone}>{s.label}</Status>
                  )}
                </li>
              );
            })}
          </ul>
        ))}

      </div>

      {signing && <ActSignDialog act={signing} onClose={() => setSigning(undefined)} />}
      {picking && (
        <Dialog open onClose={() => setPicking(false)} title="Какой счёт оплатить?">
          <ul className="divide-y divide-line p-2">
            {due.map((i) => (
              <li key={i.id}>
                <button
                  className="flex w-full items-center justify-between gap-4 rounded px-3 py-3 text-left hover:bg-panel"
                  onClick={() => {
                    setPicking(false);
                    setPaying(i);
                  }}
                >
                  <span>
                    <span className="block font-medium">Счёт № {i.number}</span>
                    <span className="block text-sm text-muted">до {formatDate(i.dueAt)}</span>
                  </span>
                  <span className="num font-semibold">{formatMoney(balanceOf(i))}</span>
                </button>
              </li>
            ))}
          </ul>
        </Dialog>
      )}
      {paying && <PayDialog invoice={paying} open onClose={() => setPaying(undefined)} />}
      {reconcile && <Reconciliation title={withActs ? 'Акт сверки взаиморасчётов' : 'Сверка расчётов'} onClose={() => setReconcile(false)} />}
    </>
  );
}

/** Statement of account (in RU — акт сверки): every invoice and payment in date order and what is left. */
function Reconciliation({ title, onClose }: { title: string; onClose: () => void }) {
  const statementQ = useStatementOfAccount();
  const { data } = statementQ;
  return (
    <Dialog open onClose={onClose} title={title} wide>
      <div className="p-5">
        {!data ? (
          <QueryPending query={statementQ} compact />
        ) : (
          <>
            <div className="overflow-x-auto"><table className="w-full min-w-[560px] text-left text-sm">
              <thead className="text-muted">
                <tr className="border-b border-line">
                  <th className="py-2 pr-3 font-normal">Дата</th>
                  <th className="py-2 pr-3 font-normal">Документ</th>
                  <th className="py-2 pr-3 text-right font-normal">Начислено</th>
                  <th className="py-2 text-right font-normal">Оплачено</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-3 text-muted">{formatDate(r.date)}</td>
                    <td className="py-2 pr-3">{r.what}</td>
                    <td className="num py-2 pr-3 text-right">{r.debit.amountMinor ? formatMoney(r.debit) : ''}</td>
                    <td className="num py-2 text-right">{r.credit.amountMinor ? formatMoney(r.credit) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            <p className="mt-4 flex justify-between border-t border-strong pt-3 font-semibold">
              <span>{data.balance.amountMinor > 0 ? 'Ваша задолженность' : 'Задолженности нет'}</span>
              <span className="num">{formatMoney(data.balance)}</span>
            </p>
            <button className="btn-secondary mt-5" title="Скачивание появится вместе с бэкендом">
              <Download className="size-4" /> Скачать PDF с подписью студии
            </button>
          </>
        )}
      </div>
    </Dialog>
  );
}
